/**
 * The route table (REQ-UI-191 gives every entry an ``errorElement``).
 *
 * Split out of :file:`main.tsx` so the table is a value a test can walk:
 * :file:`routes.test.tsx` asserts that no route lacks an error element,
 * because a single omission is enough to put react-router's default
 * "Unexpected Application Error!" page in front of an operator.
 *
 * Shape:
 *
 * - ``/`` — :class:`AppShell` (top bar + canvas slot), with
 *   - the index route mounting the multi-pane workspace (ADR-024 §2),
 *   - ``/403`` and the ``*`` catch-all (REQ-UI-037 generic copy).
 *   Each child carries its own fallback so a canvas crash keeps the chrome;
 *   the layout route's fallback is the last resort if the shell itself
 *   throws.
 * - ``/login/callback`` — rendered outside the shell so the brief
 *   redirect-and-bootstrap window doesn't flash the chrome; its fallback
 *   links back to ``/`` to restart the OIDC flow instead of replaying a
 *   spent code.
 *
 * Cross-references: ADR-024 §2; REQ-UI-191; REQ-UI-037;
 * docs/engineering/detail/storyboards/flows/01-login (Flow 01).
 */
import type { RouteObject } from "react-router";
import { AppShell } from "@/chrome/app-shell";
import { CallbackPage } from "@/auth/callback-page";
import { ForbiddenPage } from "@/auth/forbidden-page";
import { NotFoundPage } from "@/chrome/not-found-page";
import { RouteErrorFallback } from "@/chrome/route-error-fallback";
import { MultiPaneWorkspace } from "@/multi-pane/multi-pane-workspace";

export const appRoutes: RouteObject[] = [
  {
    path: "/",
    element: <AppShell />,
    // The shell itself threw: nothing of the chrome survives, so this one
    // uses the page-scope copy and offers only a reload.
    errorElement: <RouteErrorFallback surface="Meridian" scope="page" />,
    children: [
      {
        index: true,
        element: <MultiPaneWorkspace />,
        errorElement: <RouteErrorFallback surface="workspace" />,
      },
      {
        path: "403",
        element: <ForbiddenPage />,
        errorElement: <RouteErrorFallback surface="access page" />,
      },
      {
        path: "*",
        element: <NotFoundPage />,
        errorElement: <RouteErrorFallback surface="page" />,
      },
    ],
  },
  {
    path: "/login/callback",
    element: <CallbackPage />,
    errorElement: (
      <RouteErrorFallback
        surface="sign-in page"
        primaryAction={{ label: "Return to Meridian", href: "/" }}
      />
    ),
  },
];
